// A line as a position: where its moves end up, and how they are written down.
//
// Both things are asked for by a row in a list — the little board beside the name of a line, the
// moves under it — so neither may need a service to answer. The moves arrive in two shapes, because
// the two ends of the site speak differently: the catalog knows a line by its notation
// ("e4 c5 Nf3"), and the server's ranking of the user's own games knows one by UCI ("e2e4 c7c5").

import { Chess } from "#vendor/chessjs/chess.js";
import type { Move } from "#vendor/chessjs/chess.js";

/** The board before anybody has played: what a line with no moves shows. */
export const INITIAL_FEN = new Chess().fen();

type Pick = (legal: Move[], move: string) => Move | undefined;

const bySan: Pick = (legal, san) => legal.find((move) => move.san === san);

const byUci: Pick = (legal, uci) => legal.find((move) =>
    move.from === uci.slice(0, 2) && move.to === uci.slice(2, 4)
    && (uci.length < 5 || (move.promotion || "") === uci[4]));

/**
 * Replays as much of a line as is legal and hands back the game it reached. A line from outside —
 * a suggestion of the server, a catalog file — may not be playable to the end, and a list of rows
 * has to draw something either way, so the first move that does not fit ends the replay.
 */
function replay(moves: string, pick: Pick): Chess {
    const chess = new Chess();
    for (const move of (moves || "").trim().split(/\s+/)) {
        if (!move) break;
        // chess.js throws on an illegal move: look the move up instead of trying it
        const legal = pick(chess.moves({ verbose: true }) as Move[], move);
        if (!legal) break;
        chess.move(legal.san);
    }
    return chess;
}

/** "e2e4 c7c5" -> ["e4", "c5"]. */
export function sanFromUci(uciMoves: string): string[] {
    return replay(uciMoves, byUci).history() as string[];
}

/** The moves as they are written down: "1.e4 c5 2.Nf3". */
export function numberedMoves(sanMoves: string): string {
    const moves = (sanMoves || "").trim();
    if (!moves) return "";
    return moves.split(/\s+/)
        .map((san, i) => (i % 2 === 0 ? `${i / 2 + 1}.${san}` : san))
        .join(" ");
}

/** The same, from UCI. Falls back to the UCI itself when the moves cannot be replayed at all. */
export function numberedFromUci(uciMoves: string): string {
    const san = sanFromUci(uciMoves);
    return san.length ? numberedMoves(san.join(" ")) : uciMoves;
}

/**
 * The moves that tell a line apart from the one it hangs off: "4...Nf6 5.c3".
 *
 * It is what a row says when the opening table gives it the same name as its parent — which happens
 * whenever the table stops naming things before the player stops choosing them. The numbering starts
 * where the parent ended, and a suffix that starts on Black's move says so with "4...".
 */
export function movesSince(uciMoves: string, afterPly: number): string {
    const san = sanFromUci(uciMoves);
    if (afterPly < 0 || afterPly >= san.length) return "";
    return san.slice(afterPly)
        .map((move, index) => {
            const ply = afterPly + index;
            const number = Math.floor(ply / 2) + 1;
            if (ply % 2 === 0) return `${number}.${move}`;
            return index === 0 ? `${number}...${move}` : move;
        })
        .join(" ");
}

/** How many half moves a line is. */
export function plyOf(uciMoves: string): number {
    const moves = (uciMoves || "").trim();
    return moves ? moves.split(/\s+/).length : 0;
}

/** Where a line written in notation ends up. */
export function fenAfterSan(sanMoves: string): string {
    return replay(sanMoves, bySan).fen();
}

/** "e4 c5" -> "e2e4 c7c5": as much of it as is legal. */
export function uciFromSan(sanMoves: string): string {
    return (replay(sanMoves, bySan).history({ verbose: true }) as Move[])
        .map((move) => `${move.from}${move.to}${move.promotion ?? ""}`).join(" ");
}

/** Where a line written in UCI ends up. */
export function fenAfterUci(uciMoves: string): string {
    return replay(uciMoves, byUci).fen();
}
